import { createContext, useContext, type ReactNode } from 'react';
import type { MeResponse, User } from '@cleartask/shared';

interface SessionValue {
  me: MeResponse;
  /**
   * Substitui o usuario da sessao pelo que o servidor devolveu.
   *
   * A sessao e resolvida uma vez, na entrada do app, e nao e uma consulta do
   * TanStack Query — entao gravar preferencias precisava de um caminho de volta
   * explicito. Sem ele, trocar o tema mudaria o banco e nao a tela, ate alguem
   * recarregar a pagina.
   */
  updateUser: (user: User) => void;
}

/**
 * Sessao resolvida — disponivel para toda a arvore depois do `GET /me`.
 *
 * Existe principalmente por causa do FUSO: praticamente toda tela precisa de
 * `user.timezone` para decidir o que e "hoje", e passar isso por props
 * atravessaria varios niveis de componente so para chegar num rotulo de data.
 *
 * O contexto nao tem valor padrao de proposito. Um default silencioso faria um
 * componente montado fora do provider cair no fuso do dispositivo em vez de
 * falhar — e o sintoma apareceria como tarefa no grupo errado, nao como erro.
 */
const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({
  value,
  onUserChange,
  children,
}: {
  value: MeResponse;
  onUserChange: (user: User) => void;
  children: ReactNode;
}) {
  return (
    <SessionContext.Provider value={{ me: value, updateUser: onUserChange }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): MeResponse {
  const session = useContext(SessionContext);
  if (!session) {
    throw new Error('useSession precisa estar dentro de <SessionProvider>');
  }
  return session.me;
}

/** Caminho de escrita da sessao. Usado pela tela de configuracoes. */
export function useUpdateSessionUser(): (user: User) => void {
  const session = useContext(SessionContext);
  if (!session) {
    throw new Error('useUpdateSessionUser precisa estar dentro de <SessionProvider>');
  }
  return session.updateUser;
}

export function useCurrentUser(): User {
  return useSession().user;
}

/** Fuso autoritativo da aplicacao — `users.timezone`, nunca o do dispositivo. */
export function useTimeZone(): string {
  return useSession().user.timezone;
}
